import Pusher from "pusher-js";
import {AGENT_PUSHER_KEY, AGENT_PUSHER_CLUSTER, SUPPORT_PUSHER_KEY, SUPPORT_PUSHER_CLUSTER, API_URL} from "../settings/local_settings";
import {LocalStorage} from "../utils/LocalStorage";
import {APIClient, DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE} from "../utils/APIClient";

Pusher.logToConsole = false;

// Claves LÓGICAS de canal, no nombres. El nombre real lo arma el backend y lleva la agencia
// (`bu-dashboard-sync-<agency_id>`): antes esto era un canal global y a cada usuario le
// llegaban los eventos de TODAS las agencias, que es lo que agotaba la cuota diaria de la
// cuenta free de Pusher a media tarde. Tienen que coincidir con las keys que devuelve
// `realtime/config/`.
export const WebSocketChannels = {
    BU_DASHBOARD_SYNC: 'dashboard_sync',
    URL_STATUS_CHANGE: 'urls_sync'
};

export const WebSocketEvents = {
    BU_DASHBOARD_SYNCED: 'bu-dashboard-synced',
    URL_STATUS_CHANGE: 'bu-urls-status-synced',
    // El equipo de Dashbo le escribió a alguien de la agencia (ver SupportChatBubble). Payload vacío.
    SUPPORT_INCOMING: 'support-incoming'
};

// ── Cliente Pusher del TABLERO: configurado por el backend, conexión lazy ──
// La key ya no viene del build: hay un POOL de cuentas y cuál le toca a esta agencia (más
// los nombres de canal) lo decide el backend. Así rebalancear el reparto —o sumar una
// cuenta— no necesita rebuild del frontend.
let _dashboardPusher = null;
let _dashboardConfig = null;
let _configPromise = null;
let _configToken = null;            // token con el que se pidió la config (ver fetchConfig)
const _dashboardChannels = new Map(); // nombre real de canal -> canal de pusher
// Enganches de `bindWhenConnected`: esperan a que ALGUIEN abra la conexión, nunca la abren.
const _passiveBindings = new Set();

function disconnectDashboardPusher() {
    if (_dashboardPusher) {
        try {
            _dashboardPusher.disconnect();
        } catch (e) {
            console.error('[Pusher] disconnect failed:', e);
        }
    }
    _dashboardPusher = null;
    _dashboardConfig = null;
    _dashboardChannels.clear();
    // Siguen registrados: se vuelven a enganchar a la conexión del usuario nuevo cuando exista.
    _passiveBindings.forEach((binding) => { binding.channel = null; });
}

function attachPassiveBinding(binding) {
    const channel = dashboardChannelFor(binding.channelKey);
    if (!channel || binding.channel === channel) return;
    channel.bind(binding.eventId, binding.handler);
    binding.channel = channel;
}

/**
 * Config de realtime del usuario logueado, pedida UNA sola vez y memoizada.
 *
 * La promesa se keyea por token igual que el cache del APIClient: si el usuario se desloguea
 * y entra con otra cuenta sin recargar, la config vieja lo dejaría escuchando el canal de la
 * agencia anterior — fuga cross-agency. Al cambiar el token se descarta la conexión y se
 * vuelve a preguntar.
 *
 * El FALLO no se memoiza. `APIClient` resuelve (no rechaza) con `{status:'NETWORK_ERROR'}`, así
 * que un blip de red o un 5xx en el primer mount dejaría `null` pegado y la pestaña sin realtime
 * el resto de la sesión, aunque el backend vuelva en el acto. Mismo criterio que el cache del
 * APIClient ("no se cachean ERROR ni NETWORK_ERROR"), y acá pesa más porque no hay TTL.
 */
function fetchConfig() {
    const token = LocalStorage.fetchUserToken() || 'anon';
    if (_configPromise && _configToken === token) return _configPromise;

    disconnectDashboardPusher();
    _configToken = token;
    const promise = new APIClient(API_URL + 'realtime/config/').call('GET')
        .then((data) => (data && data.key && data.channels) ? data : null)
        .catch(() => null) // sin realtime el tablero se actualiza al recargar: degradación válida
        .then((config) => {
            // Sin config, se descarta la promesa para que el próximo bind() vuelva a intentar.
            // El `===` evita pisar una promesa más nueva (cambio de token mientras esta viajaba).
            if (!config && _configPromise === promise) _configPromise = null;
            return config;
        });
    _configPromise = promise;
    return promise;
}

function dashboardChannelFor(channelKey) {
    if (!_dashboardPusher || !_dashboardConfig) return null;
    const channelName = _dashboardConfig.channels[channelKey];
    if (!channelName) return null;
    if (!_dashboardChannels.has(channelName)) {
        _dashboardChannels.set(channelName, _dashboardPusher.subscribe(channelName));
    }
    return _dashboardChannels.get(channelName);
}

export const WebSocketsClient = {
    /**
     * Escucha `eventId` en el canal lógico `channelKey` y devuelve la función para dejar de
     * escuchar. Es asincrónico por dentro (espera la config del backend), así que puede
     * llamarse en el montaje de un componente sin esperar nada.
     */
    bind: (channelKey, eventId, handler) => {
        let unbound = false;
        let boundChannel = null;

        fetchConfig().then((config) => {
            if (unbound || !config) return;
            // La config puede llegar DESPUÉS de un cambio de usuario: la promesa vieja sigue viva
            // y sus `.then` correrían con la agencia anterior. Sin este guard, un login con otra
            // cuenta podría terminar suscripto al canal de la agencia previa.
            if (_configToken !== (LocalStorage.fetchUserToken() || 'anon')) return;
            if (!_dashboardPusher) {
                _dashboardConfig = config;
                _dashboardPusher = new Pusher(config.key, {
                    cluster: config.cluster,
                    forceTLS: true
                });
                _dashboardPusher.connection.bind('error', (err) => {
                    console.error('[Pusher] Connection error:', err);
                });
                _passiveBindings.forEach(attachPassiveBinding);
            }
            boundChannel = dashboardChannelFor(channelKey);
            if (boundChannel) boundChannel.bind(eventId, handler);
        });

        return () => {
            unbound = true;
            // Se desbindea SOLO este handler: `unbind(eventId)` sin el callback se lleva
            // puestos los de todas las demás filas del tablero, que escuchan el mismo evento
            // en el mismo canal.
            if (boundChannel) boundChannel.unbind(eventId, handler);
        };
    },

    /**
     * Como `bind`, pero NUNCA abre la conexión: se engancha sólo si una pantalla ya la abrió, y si
     * se abre más tarde, se engancha entonces. Hoy la abren sólo las filas del Tablero
     * (`DashboardRow`) y la espera del onboarding (`ApplyInterstitial`): en una pestaña que no pasó
     * por ahí —Informes, el agente, la ficha del cliente— la señal no llega y manda el polling.
     *
     * Para lo que quiere escuchar en TODA la app sin pagar una conexión de Pusher por pestaña
     * (las cuentas son free: 100 conexiones concurrentes cada una). Lo usa la burbuja de soporte,
     * que con la conexión cerrada se arregla con su polling. Devuelve la función de desenganche.
     */
    bindWhenConnected: (channelKey, eventId, handler) => {
        const binding = {channelKey, eventId, handler, channel: null};
        _passiveBindings.add(binding);
        if (_dashboardPusher && _dashboardConfig) attachPassiveBinding(binding);

        return () => {
            _passiveBindings.delete(binding);
            if (binding.channel) binding.channel.unbind(eventId, handler);
            binding.channel = null;
        };
    }
};

// Cliente Pusher separado para eventos de progreso del agente
const agentPusherClient = AGENT_PUSHER_KEY ? new Pusher(AGENT_PUSHER_KEY, {
    cluster: AGENT_PUSHER_CLUSTER,
    forceTLS: true
}) : null;

if (agentPusherClient) {
    agentPusherClient.connection.bind('connected', () => {
        console.log('[AgentPusher] Connected, socketId:', agentPusherClient.connection.socket_id);
    });
    agentPusherClient.connection.bind('error', (err) => {
        console.error('[AgentPusher] Connection error:', err);
    });
    agentPusherClient.connection.bind('state_change', (states) => {
        console.log('[AgentPusher] State:', states.previous, '->', states.current);
    });
}

export const AgentWebSocketsClient = {
    subscribe: (channelName) => {
        if (!agentPusherClient) return null;
        return agentPusherClient.subscribe(channelName);
    },
    unsubscribe: (channelName) => {
        if (agentPusherClient) agentPusherClient.unsubscribe(channelName);
    }
};

// ── Cliente Pusher #3: burbuja de chat de soporte (Reemplazo de Intercom, Fase 3) ──
// El canal es PRIVADO (private-support-agency-<agency_id>): la auth pasa por el Backend
// (POST support/pusher/auth/) con los mismos headers que APIClient (Token + X-Dashbo-Client).
//
// Diseño de conexión LAZY + efímera: la instancia de Pusher se crea recién en el primer
// subscribe y se DESTRUYE (disconnect()) al hacer unsubscribe. La conexión vive SOLO mientras
// el panel de chat está abierto — el free tier de Pusher tiene 100 conexiones concurrentes,
// así que solo las consumen los usuarios que están efectivamente chateando.
//
// Si SUPPORT_PUSHER_KEY es falsy (el caso de un build que no la hornea, típicamente `npm start`),
// subscribeAgencyChannel devuelve false y la UI cae a polling. Que onSignal nunca llegue es
// un fallback válido y silencioso — lo mismo pasa con un cluster equivocado, así que "anda pero
// por polling" NO prueba que la cuenta esté bien configurada.
let _supportPusher = null;      // instancia lazy (única mientras el panel está abierto)
let _supportChannel = null;     // canal privado suscripto
let _supportChannelName = null; // nombre del canal (para unsubscribe)

const SUPPORT_UPDATE_EVENT = 'support-update';

export const SupportWebSocketsClient = {
    isAvailable: () => !!SUPPORT_PUSHER_KEY,

    subscribeAgencyChannel: (agencyId, onSignal) => {
        if (!SUPPORT_PUSHER_KEY) return false;
        if (agencyId === null || agencyId === undefined || agencyId === '') return false;

        try {
            if (!_supportPusher) {
                _supportPusher = new Pusher(SUPPORT_PUSHER_KEY, {
                    cluster: SUPPORT_PUSHER_CLUSTER,
                    forceTLS: true,
                    channelAuthorization: {
                        endpoint: API_URL + 'support/pusher/auth/',
                        transport: 'ajax',
                        headers: {
                            'Authorization': 'Token ' + LocalStorage.fetchUserToken(),
                            [DASHBO_CLIENT_HEADER]: DASHBO_CLIENT_HEADER_VALUE,
                        },
                    },
                });
                // Errores best-effort: si la conexión falla, la UI sigue con polling.
                _supportPusher.connection.bind('error', (err) => {
                    console.error('[SupportPusher] Connection error:', err);
                });
            }

            _supportChannelName = `private-support-agency-${agencyId}`;
            _supportChannel = _supportPusher.subscribe(_supportChannelName);
            // Si el auth devuelve 403, pusher emite subscription_error — lo logueamos y la UI
            // sigue con polling (onSignal simplemente nunca se dispara).
            _supportChannel.bind('pusher:subscription_error', (status) => {
                console.error('[SupportPusher] Subscription error:', status);
            });
            _supportChannel.bind(SUPPORT_UPDATE_EVENT, (payload) => {
                if (typeof onSignal === 'function') onSignal(payload);
            });
            return true;
        } catch (e) {
            console.error('[SupportPusher] subscribe failed:', e);
            return false;
        }
    },

    unsubscribeAgencyChannel: () => {
        try {
            if (_supportPusher) {
                if (_supportChannelName) {
                    _supportPusher.unsubscribe(_supportChannelName);
                }
                // La conexión vive solo mientras el panel está abierto: cerramos y descartamos
                // la instancia para liberar el slot de conexión concurrente del free tier.
                _supportPusher.disconnect();
            }
        } catch (e) {
            console.error('[SupportPusher] unsubscribe failed:', e);
        } finally {
            _supportPusher = null;
            _supportChannel = null;
            _supportChannelName = null;
        }
    },
};